import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, LocationOn, Restaurant, Search } from '@mui/icons-material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

type KitchenType = 'central' | 'satellite';
type KitchenStatus = 'active' | 'inactive' | 'maintenance' | 'offline';

interface KitchenProfile {
  id: string;
  kitchenCode: string;
  kitchenName: string;
  kitchenType: KitchenType;
  status: KitchenStatus;
  locationId?: string;
  locationCode?: string;
  locationName?: string;
  latitude?: number;
  longitude?: number;
  dailyMealCapacity?: number;
  managerName?: string;
  managerPhone?: string;
  centralWarehouseLocationId?: string;
  kitchenStoreLocationId?: string;
  shopfloorLocationId?: string;
  schools?: number;
  stationCount?: number;
  metadata?: Record<string, unknown>;
}

interface MasterLocation {
  id: string;
  locationCode: string;
  name: string;
  locationType: string;
  status: 'active' | 'inactive' | 'maintenance';
  restrictions?: any;
}

interface KitchenRow {
  id: string;
  locationId: string;
  profileId?: string;
  kitchenCode: string;
  kitchenName: string;
  kitchenType: KitchenType;
  status: KitchenStatus;
  latitude?: number;
  longitude?: number;
  dailyMealCapacity?: number;
  managerName?: string;
  managerPhone?: string;
  centralWarehouseLocationId?: string;
  kitchenStoreLocationId?: string;
  shopfloorLocationId?: string;
  schools?: number;
  stationCount?: number;
}

const defaultKitchen: Partial<KitchenRow> = {
  kitchenCode: '',
  kitchenName: '',
  kitchenType: 'satellite',
  status: 'active',
  dailyMealCapacity: 0,
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const message = err.response?.data?.message;
    return Array.isArray(message) ? message.join(', ') : message || err.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const toNumberOrUndefined = (value: unknown): number | undefined => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const asList = (data: any) => Array.isArray(data) ? data : data?.data ?? [];

const flattenLocations = (locations: any[]): MasterLocation[] => {
  const result: MasterLocation[] = [];
  const visit = (location: any) => {
    result.push(location);
    (location.childLocations || location.children || []).forEach(visit);
  };
  locations.forEach(visit);
  return result;
};

const KitchensTab: React.FC = () => {
  const [rows, setRows] = useState<KitchenRow[]>([]);
  const [locations, setLocations] = useState<MasterLocation[]>([]);
  const [profiles, setProfiles] = useState<KitchenProfile[]>([]);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<KitchenRow | null>(null);
  const [form, setForm] = useState<Partial<KitchenRow>>(defaultKitchen);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<KitchenRow | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const locationOptions = useMemo(() => locations.filter((location) => location.id !== form.locationId), [locations, form.locationId]);

  const buildRows = useCallback((locationList: MasterLocation[], profileList: KitchenProfile[]): KitchenRow[] => {
    return locationList
      .filter((location) => location.locationType === 'kitchen' || location.restrictions?.profileType === 'kitchen')
      .map((location) => {
        const profile = profileList.find((item) => item.locationId === location.id);
        return {
          id: location.id,
          locationId: location.id,
          profileId: profile?.id,
          kitchenCode: location.locationCode,
          kitchenName: location.name,
          kitchenType: (location.restrictions?.kitchenType || profile?.kitchenType || 'satellite') as KitchenType,
          status: (profile?.status || location.status || 'active') as KitchenStatus,
          latitude: location.restrictions?.latitude ?? profile?.latitude,
          longitude: location.restrictions?.longitude ?? profile?.longitude,
          dailyMealCapacity: profile?.dailyMealCapacity,
          managerName: profile?.managerName,
          managerPhone: profile?.managerPhone,
          centralWarehouseLocationId: profile?.centralWarehouseLocationId,
          kitchenStoreLocationId: profile?.kitchenStoreLocationId,
          shopfloorLocationId: profile?.shopfloorLocationId,
          schools: profile?.schools,
          stationCount: profile?.stationCount,
        };
      });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [locationResp, profileResp] = await Promise.all([
        axios.get(`${API_BASE}/master-data/locations`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/kitchen-ops/kitchens`, { headers: authHeaders() }),
      ]);
      const locationList = flattenLocations(asList(locationResp.data));
      const profileList = asList(profileResp.data);
      setLocations(locationList);
      setProfiles(profileList);
      setRows(buildRows(locationList, profileList));
    } catch (err) {
      setError(apiError(err, 'Failed to load kitchens'));
    } finally {
      setLoading(false);
    }
  }, [buildRows]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = rows.filter((row) => {
    const query = searchText.toLowerCase();
    const matchesSearch = !query ||
      row.kitchenCode.toLowerCase().includes(query) ||
      row.kitchenName.toLowerCase().includes(query);
    const matchesType = !typeFilter || row.kitchenType === typeFilter;
    return matchesSearch && matchesType;
  });

  const openAddDialog = () => {
    setEditing(null);
    setForm(defaultKitchen);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (row: KitchenRow) => {
    setEditing(row);
    setForm({ ...row });
    setFormErrors({});
    setDialogOpen(true);
  };

  const updateForm = (field: keyof KitchenRow, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!form.kitchenCode?.trim()) errors.kitchenCode = 'Code is required';
    if (!form.kitchenName?.trim()) errors.kitchenName = 'Name is required';
    const latitude = toNumberOrUndefined(form.latitude);
    const longitude = toNumberOrUndefined(form.longitude);
    if (form.latitude != null && latitude == null) errors.latitude = 'Invalid latitude';
    if (form.longitude != null && longitude == null) errors.longitude = 'Invalid longitude';
    if (latitude != null && (latitude < -90 || latitude > 90)) errors.latitude = 'Latitude must be between -90 and 90';
    if (longitude != null && (longitude < -180 || longitude > 180)) errors.longitude = 'Longitude must be between -180 and 180';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const locationPayload = () => ({
    locationCode: form.kitchenCode,
    name: form.kitchenName,
    locationType: 'kitchen',
    status: form.status === 'maintenance' ? 'maintenance' : form.status === 'inactive' || form.status === 'offline' ? 'inactive' : 'active',
    restrictions: {
      domain: 'kitchen-ops',
      profileType: 'kitchen',
      kitchenType: form.kitchenType || 'satellite',
      latitude: toNumberOrUndefined(form.latitude),
      longitude: toNumberOrUndefined(form.longitude),
    },
  });

  const profilePayload = (location: MasterLocation) => ({
    kitchenCode: location.locationCode,
    kitchenName: location.name,
    kitchenType: form.kitchenType || 'satellite',
    status: form.status || 'active',
    locationId: location.id,
    locationCode: location.locationCode,
    locationName: location.name,
    latitude: toNumberOrUndefined(form.latitude),
    longitude: toNumberOrUndefined(form.longitude),
    dailyMealCapacity: Number(form.dailyMealCapacity || 0),
    managerName: form.managerName || undefined,
    managerPhone: form.managerPhone || undefined,
    centralWarehouseLocationId: form.centralWarehouseLocationId || undefined,
    kitchenStoreLocationId: form.kitchenStoreLocationId || undefined,
    shopfloorLocationId: form.shopfloorLocationId || undefined,
    metadata: { masterDataLocationType: 'kitchen' },
  });

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      let location: MasterLocation;
      if (editing) {
        const response = await axios.patch(`${API_BASE}/master-data/locations/${editing.locationId}`, locationPayload(), { headers: authHeaders() });
        location = response.data;
      } else {
        const response = await axios.post(`${API_BASE}/master-data/locations`, locationPayload(), { headers: authHeaders() });
        location = response.data;
      }

      const existingProfile = editing?.profileId ? profiles.find((profile) => profile.id === editing.profileId) : undefined;
      if (existingProfile) {
        await axios.patch(`${API_BASE}/kitchen-ops/kitchens/${existingProfile.id}`, {
          ...profilePayload(location),
          updatedBy: 'frontend',
        }, { headers: authHeaders() });
      } else {
        await axios.post(`${API_BASE}/kitchen-ops/kitchens`, {
          ...profilePayload(location),
          createdBy: 'frontend',
        }, { headers: authHeaders() });
      }

      setSnackbar({ open: true, message: `Kitchen ${editing ? 'updated' : 'created'}`, severity: 'success' });
      setDialogOpen(false);
      await load();
    } catch (err) {
      setSnackbar({ open: true, message: apiError(err, 'Failed to save kitchen'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const deleteKitchen = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      if (deleteTarget.profileId) {
        await axios.delete(`${API_BASE}/kitchen-ops/kitchens/${deleteTarget.profileId}`, { headers: authHeaders() });
      }
      await axios.delete(`${API_BASE}/master-data/locations/${deleteTarget.locationId}`, { headers: authHeaders() });
      setSnackbar({ open: true, message: 'Kitchen deleted', severity: 'success' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setSnackbar({ open: true, message: apiError(err, 'Failed to delete kitchen'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'kitchenCode', headerName: 'Location Code', width: 140, renderCell: (params) => <Typography variant="body2" fontWeight={600}>{params.value}</Typography> },
    { field: 'kitchenName', headerName: 'Kitchen Location', flex: 1, minWidth: 220 },
    { field: 'kitchenType', headerName: 'Kitchen Type', width: 140, renderCell: (params) => <Chip label={String(params.value).toUpperCase()} size="small" variant="outlined" /> },
    { field: 'status', headerName: 'Status', width: 130, renderCell: (params) => <Chip label={String(params.value).toUpperCase()} size="small" color={params.value === 'active' ? 'success' : params.value === 'maintenance' ? 'warning' : 'default'} /> },
    { field: 'gps', headerName: 'GPS', width: 170, renderCell: (params) => params.row.latitude != null && params.row.longitude != null ? `${params.row.latitude}, ${params.row.longitude}` : '-' },
    { field: 'dailyMealCapacity', headerName: 'Capacity', width: 110, type: 'number' },
    { field: 'schools', headerName: 'Schools', width: 90, type: 'number' },
    { field: 'stationCount', headerName: 'Stations', width: 90, type: 'number' },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem icon={<Edit />} label="Edit" onClick={() => openEditDialog(params.row)} key="edit" />,
        <GridActionsCellItem icon={<Delete />} label="Delete" onClick={() => setDeleteTarget(params.row)} key="delete" color="error" />,
      ],
    },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search kitchen locations..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Kitchen Type</InputLabel>
            <Select value={typeFilter} label="Kitchen Type" onChange={(event) => setTypeFilter(event.target.value)}>
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="central">Central</MenuItem>
              <MenuItem value="satellite">Satellite</MenuItem>
            </Select>
          </FormControl>
          <Box flex={1} />
          <Button variant="outlined" startIcon={<LocationOn />} onClick={load} disabled={loading}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>Add Kitchen Location</Button>
        </Toolbar>
      </Paper>

      <Paper sx={{ height: 540 }}>
        <DataGrid rows={filteredRows} columns={columns} loading={loading} pageSizeOptions={[10, 25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick density="compact" />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Restaurant />
            {editing ? `Edit Kitchen Location: ${editing.kitchenCode}` : 'Add Kitchen Location'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                Kitchen is a Location type. Central or satellite is the kitchen subtype.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Location Code *" value={form.kitchenCode || ''} onChange={(event) => updateForm('kitchenCode', event.target.value)} error={!!formErrors.kitchenCode} helperText={formErrors.kitchenCode} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Kitchen Location Name *" value={form.kitchenName || ''} onChange={(event) => updateForm('kitchenName', event.target.value)} error={!!formErrors.kitchenName} helperText={formErrors.kitchenName} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Kitchen Type</InputLabel>
                <Select label="Kitchen Type" value={form.kitchenType || 'satellite'} onChange={(event) => updateForm('kitchenType', event.target.value)}>
                  <MenuItem value="central">Central Kitchen</MenuItem>
                  <MenuItem value="satellite">Satellite Kitchen</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status || 'active'} onChange={(event) => updateForm('status', event.target.value)}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Meals/day Capacity" type="number" value={form.dailyMealCapacity ?? ''} onChange={(event) => updateForm('dailyMealCapacity', event.target.value)} fullWidth size="small" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Latitude" type="number" value={form.latitude ?? ''} onChange={(event) => updateForm('latitude', event.target.value)} error={!!formErrors.latitude} helperText={formErrors.latitude} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Longitude" type="number" value={form.longitude ?? ''} onChange={(event) => updateForm('longitude', event.target.value)} error={!!formErrors.longitude} helperText={formErrors.longitude} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Manager" value={form.managerName || ''} onChange={(event) => updateForm('managerName', event.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Manager Phone" value={form.managerPhone || ''} onChange={(event) => updateForm('managerPhone', event.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Central Warehouse</InputLabel>
                <Select label="Central Warehouse" value={form.centralWarehouseLocationId || ''} onChange={(event) => updateForm('centralWarehouseLocationId', event.target.value || undefined)}>
                  <MenuItem value="">Not assigned</MenuItem>
                  {locationOptions.map((location) => <MenuItem key={location.id} value={location.id}>{location.name} ({location.locationCode})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Kitchen Store</InputLabel>
                <Select label="Kitchen Store" value={form.kitchenStoreLocationId || ''} onChange={(event) => updateForm('kitchenStoreLocationId', event.target.value || undefined)}>
                  <MenuItem value="">Not assigned</MenuItem>
                  {locationOptions.map((location) => <MenuItem key={location.id} value={location.id}>{location.name} ({location.locationCode})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Shopfloor Location</InputLabel>
                <Select label="Shopfloor Location" value={form.shopfloorLocationId || ''} onChange={(event) => updateForm('shopfloorLocationId', event.target.value || undefined)}>
                  <MenuItem value="">Not assigned</MenuItem>
                  {locationOptions.map((location) => <MenuItem key={location.id} value={location.id}>{location.name} ({location.locationCode})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving}>{editing ? 'Update' : 'Create'} Kitchen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Kitchen Location</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.kitchenName}</strong> ({deleteTarget?.kitchenCode})?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</Button>
          <Button onClick={deleteKitchen} variant="contained" color="error" disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default KitchensTab;
